import { useEffect, useState } from "react";
import axios from "axios";
import { Phone, Sparkles, Check, Printer, Scissors } from "lucide-react";
import { API, fmtMoney } from "@/lib/data";

const FALLBACK = [
  { name: "Front Roof Line & Entryway", price: 599 },
  { name: "Estate Roofline & Windows", price: 999 },
  { name: "The Signature Grand Estate", price: 1499 },
];

const TEAR_OFFS = [...Array(8)];

export default function Flyer() {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings`).then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const packages = settings?.packages?.length ? settings.packages : FALLBACK;
  const phone = settings?.phone_display || "(678) 254-4892";

  return (
    <main className="flex min-h-screen flex-col items-center bg-[#04100A] py-10 print:bg-white print:py-0" data-testid="flyer-page">
      <button
        onClick={() => window.print()}
        data-testid="flyer-print-button"
        className="mb-6 flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-500 to-amber-400 px-8 py-3 text-sm font-bold uppercase tracking-widest text-stone-950 gold-glow print:hidden"
      >
        <Printer size={15} /> Print Flyer
      </button>

      <div
        className="relative flex w-[8.5in] flex-col overflow-hidden bg-[#0B1D14] text-amber-50 shadow-2xl print:shadow-none"
        style={{ height: "11in" }}
        data-testid="flyer-sheet"
      >
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-amber-500/15 blur-3xl" />
        <div className="pointer-events-none absolute -left-24 top-1/3 h-72 w-72 rounded-full bg-amber-500/10 blur-3xl" />

        <div className="flex items-center justify-between border-b border-amber-500/25 px-10 py-5">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-amber-600">
              <Sparkles size={18} className="text-stone-950" />
            </span>
            <span className="font-display text-xl font-bold">Southern Christmas Co.</span>
          </div>
          <span className="font-mono-x text-[10px] uppercase tracking-[0.25em] text-amber-300">Licensed & Insured</span>
        </div>

        <div className="px-10 pt-6 text-center">
          <p className="font-mono-x text-[11px] uppercase tracking-[0.35em] text-amber-400">Professional Christmas Light Installation</p>
          <h1 className="font-display mt-3 text-5xl font-black leading-[1.05]">
            Your Home, Wrapped in<br />
            <span className="italic text-amber-400">Warm Winter Light.</span>
          </h1>
        </div>

        <div className="mx-10 mt-5 overflow-hidden rounded-2xl border border-amber-500/30">
          <div className="relative h-44">
            <img
              src="/images/signature.png"
              alt="Home fully lit with warm Christmas lights by Southern Christmas Co."
              className="h-full w-full object-cover"
              style={{ objectPosition: "center 62%" }}
              data-testid="flyer-house-photo"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#06130D]/70 via-transparent to-transparent" />
            <span className="absolute bottom-3 left-4 rounded-full bg-[#06130D]/80 px-4 py-1.5 font-mono-x text-[9px] uppercase tracking-[0.2em] text-amber-300">
              We design, install & take it all down — you never touch a ladder
            </span>
          </div>
        </div>

        <div className="mx-10 mt-5 grid grid-cols-3 gap-3">
          {packages.map((p) => (
            <div key={p.name} className="rounded-2xl border border-amber-500/30 bg-[#06130D]/70 p-4 text-center">
              <p className="font-display text-2xl font-black text-amber-400">{fmtMoney(p.price)}</p>
              <p className="mt-1 text-[11px] font-semibold leading-tight">{p.name}</p>
              <p className="mt-1.5 font-mono-x text-[8px] uppercase tracking-[0.15em] text-emerald-100/50">Install + takedown incl.</p>
            </div>
          ))}
        </div>

        <div className="mx-10 mt-4 flex flex-wrap justify-center gap-x-6 gap-y-1.5">
          {["Commercial warm-white LEDs", "48-hour storm repair", "January takedown included"].map((t) => (
            <span key={t} className="flex items-center gap-1.5 text-[11px] text-emerald-100/75">
              <Check size={12} className="text-amber-400" /> {t}
            </span>
          ))}
        </div>

        <div className="mx-10 mt-5 flex items-center gap-8 rounded-3xl border border-amber-500/30 bg-[#06130D]/70 p-6">
          <div className="flex-1">
            <p className="font-mono-x text-[10px] uppercase tracking-[0.3em] text-amber-400">Book your date today</p>
            <p className="mt-2 text-sm leading-relaxed text-emerald-100/80">
              Scan the code or call — pick your package, choose your install day, and lock it in with a 50% deposit.
              November dates fill fast.
            </p>
            <a href="tel:6782544892" className="font-display mt-4 block text-4xl font-black tracking-tight text-amber-50" data-testid="flyer-phone">
              {phone}
            </a>
            <p className="mt-1 font-mono-x text-[10px] uppercase tracking-[0.25em] text-emerald-100/50">Call or text · Atlanta & North Georgia</p>
          </div>
          <div className="flex flex-col items-center">
            <div className="rounded-2xl bg-white p-3 gold-glow" data-testid="flyer-qr-frame">
              <img src="/images/qr-code.png" alt="Scan to visit Southern Christmas Co." className="h-44 w-44" data-testid="flyer-qr-code" />
            </div>
            <p className="mt-3 max-w-[160px] text-center font-mono-x text-[9px] uppercase leading-relaxed tracking-[0.2em] text-amber-300">
              Scan to see packages & book your spot
            </p>
          </div>
        </div>

        <div className="mt-auto">
          <div className="mx-10 flex items-center gap-2 border-t border-dashed border-amber-500/40 pt-2 text-emerald-100/40">
            <Scissors size={14} />
            <span className="font-mono-x text-[9px] uppercase tracking-[0.3em]">Tear here</span>
          </div>
          <div className="flex h-24 items-stretch justify-between gap-1 px-10 pb-4 pt-2" data-testid="flyer-tearoffs">
            {TEAR_OFFS.map((_, i) => (
              <div key={i} className="flex flex-1 items-center justify-center rounded-b-lg border border-t-0 border-amber-500/25 bg-[#06130D]/60">
                <span className="rotate-180 font-mono-x text-[10px] font-semibold tracking-[0.15em] text-amber-300 [writing-mode:vertical-rl]">
                  {phone} · Southern Christmas Co.
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
